import React, { ReactNode, useMemo } from 'react';
import {
    FormErrorCode,
    ProjectTheme,
    FormControllerConfigProvider,
    FormController,
    FormRuntimeProvider,
    FormStructureSpecificsBuilder,
    InitInfo,
    IWidget,
    PutFormValuesEnvelope,
    FormValuesEnvelope,
    IFormDef,
} from '@truvity/widget__forms';
import { Stack } from '@mui/material';
import { JsonSchemaErrorMessages } from '@truvity/widget__json-schema';

import { disableViewOnlyForm } from './helpers/form';
import { OverridableLocalizationProvider } from './components/OverridableLocalizationProvider';

type EntityId = string;

type SlotRenderProps = {
    revision: number;
    isSubmitting: boolean;
    showErrors: () => void;
};

export type WalletFormProps = {
    autoSave?: boolean;
    theme: ProjectTheme;
    entityId: EntityId;
    defs: IFormDef;
    convertToApiType: (values: any) => any;
    getForm: () => Promise<FormValuesEnvelope>;
    putForm?: (payload: PutFormValuesEnvelope) => Promise<void>;
    onInit?: (info: InitInfo) => void;
    footerSlot?: (props: SlotRenderProps) => ReactNode;
    headerSlot?: (props: SlotRenderProps) => ReactNode;
    onRevisionMismatch?: () => void;
    widgets?: IWidget[];
};

export const WalletForm = ({
    theme,
    autoSave,
    entityId,
    defs,
    convertToApiType,
    getForm,
    putForm,
    onInit,
    headerSlot,
    footerSlot,
    onRevisionMismatch,
    widgets = [],
}: WalletFormProps) => {
    const formSpecifics = useMemo(() => new FormStructureSpecificsBuilder(defs), [defs]);
    const isViewOnly = !putForm;

    return (
        <OverridableLocalizationProvider>
            <FormRuntimeProvider>
                <FormControllerConfigProvider
                    theme={theme}
                    errors={{
                        ...JsonSchemaErrorMessages,
                        [FormErrorCode.REQUIRED]: 'This field is required',
                        [FormErrorCode.MUSTBEEMPTY]: 'Must be empty',
                    }}
                >
                    <FormController
                        autoSave={autoSave}
                        identity={{ id: defs.getName(), entrypoint: entityId }}
                        structure={defs}
                        specifics={formSpecifics.getDescriptor()}
                        formatValues={convertToApiType}
                        api={{
                            getForm,
                            putForm: async (_identity, values) => putForm?.(values),
                        }}
                        widgets={widgets}
                        effects={isViewOnly ? [disableViewOnlyForm] : undefined}
                        onInit={onInit}
                        onRevisionMismatch={onRevisionMismatch}
                    >
                        {({ formFields, submitting, showErrors, valuesRevision }) => {
                            const slotProps: SlotRenderProps = {
                                isSubmitting: submitting ?? false,
                                showErrors,
                                revision: valuesRevision,
                            };

                            return (
                                <Stack spacing={1}>
                                    <div>{headerSlot?.(slotProps)}</div>
                                    {formFields}
                                    <div>{footerSlot?.(slotProps)}</div>
                                </Stack>
                            );
                        }}
                    </FormController>
                </FormControllerConfigProvider>
            </FormRuntimeProvider>
        </OverridableLocalizationProvider>
    );
};
